import React, { useState, useEffect } from 'react';
import '../../Assets/css/resptable.css';
import { ToastContainer, toast } from 'react-toastify';

import useViewApplicationModal from './useViewApplication';
import ViewApplicationModal from './ViewExternalCreditApplication';
import PDFModal from '../PDFModal.js';

export default function ViewExternalCreditApplicationUpdate({
    showEditModal,
    closeEditModal,
    accountId,
    creditId,
    role,
    http,
    onUpdateSuccess,
    onRefreshData
}) {
    // State for application data
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        application_id: null,
        course_name: '',
        course_code: '',
        course_category: '',
        course_level: '',
        platform: '',
        mode: '',
        acquired_credits: '',
        grade: '',
        grade_point: '',
        grade_scale: '',
        percentage: '',
        principal_remarks: '',
        aca16_remarks: '',
        documents: []
    });
    const [studentData, setStudentData] = useState(null);
    //const [student, setStudentData1] = useState(null);
    const [studentApplicationData, setStudentApplicationData] = useState(null);
    const [applicationLabel, setApplicationLabel] = useState('');
    const [nheqfOptions, setNheqfOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);

    const [submitLoading, setSubmitLoading] = useState(false);
    const [editError, setEditError] = useState(null);
    const [removingDoc, setRemovingDoc] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [documents, setDocuments] = useState([]);

    const student = {
        accountid: studentData?.accountid,
        id: studentApplicationData?.application_id
    };
    const {
        showViewModal,
        viewData,
        viewLoading,
        pdfModal,
        pdfLoadingId,
        openPdfModal,
        openViewModal,
        closeViewModal,
        closePdfModal
    } = useViewApplicationModal(http);

    useEffect(() => {
        return () => {
            if (pdfModal.url) URL.revokeObjectURL(pdfModal.url);
        };
    }, [pdfModal.url]);


    const modeOptions = ['ONLINE', 'OFFLINE', 'HYBRID'];

    // Fetch application details when modal opens
    useEffect(() => {
        if (showEditModal && accountId && creditId && !dataLoaded) {
            fetchApplicationDetails();
        }
    }, [showEditModal, accountId, creditId, dataLoaded]);

    const fetchApplicationDetails = () => {
        setLoading(true);
        setEditError(null);

        http.post('/College/getStudentCreditApplication', {
            account_id: accountId,
            credit_id: creditId
        })
            .then((res) => {
                if (res.data.status) {
                    const data = res.data.data;

                    if (data.student) {
                        setStudentData(data.student);
                        //setStudentData1(data.student);
                        setStudentApplicationData(data.applications[0]);

                    }

                    if (data.applications && data.applications.length > 0) {
                        const app = data.applications[0];

                        // Parse remarks JSON and separate by role
                        let allRemarks = [];
                        if (app.remarks) {
                            try {
                                allRemarks = typeof app.remarks === 'string'
                                    ? JSON.parse(app.remarks)
                                    : app.remarks;
                            } catch (e) {
                                allRemarks = [];
                            }
                        }

                        // Get latest remark per role
                        const principalRemark = [...allRemarks].reverse().find(r => r.name === 'Principal')?.remark || '';
                        const aca16Remark = [...allRemarks].reverse().find(r => r.name === 'ACA16')?.remark || '';

                        setFormData({
                            application_id: app.application_id || null,
                            course_name: app.course_name || '',
                            course_code: app.course_code || '',
                            course_category: app.course_category || '',
                            course_level: app.course_level || '',
                            platform: app.platform || '',
                            mode: app.mode || '',
                            acquired_credits: app.acquired_credits || '',
                            grade: app.grade || '',
                            grade_point: app.grade_point || '',
                            grade_scale: app.grade_scale || '',
                            percentage: app.percentage || '',
                            principal_remarks: principalRemark,
                            aca16_remarks: aca16Remark,
                            documents: app.documents || []
                        });
                        setApplicationLabel(app.application_label || '');
                        //STORE DOCUMENTS SEPARATELY
                        setDocuments(app.documents || []);
                    }

                    setNheqfOptions(data.nheqf_levels || []);

                    setCategoryOptions(
                        (data.category || []).map(item => ({
                            value: item.id,
                            label: item.category_code
                        }))
                    );
                    setDataLoaded(true);
                } else {
                    setEditError('Failed to load application details');
                }
            })
            .catch((err) => {
                console.error('Error fetching application details:', err);
                setEditError('Something went wrong while loading application details');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // For acquired_credits, keep as string
        if (name === 'acquired_credits') {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        } else if (name === 'grade_point') {
            // grade_point should be integer
            setFormData(prev => ({
                ...prev,
                [name]: value === '' ? '' : parseInt(value)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setEditError(null);
    };

    const removeDocument = (docIndex, docId) => {
        if (!window.confirm('Are you sure you want to remove this document?')) return;

        setRemovingDoc(docId);

        http.post('/college/removedoc', {
            document_id: docId,
            application_id: formData.application_id
        })
            .then((result) => {
                if (result.data.status === true) {
                    setFormData(prev => ({
                        ...prev,
                        documents: prev.documents.filter((_, idx) => idx !== docIndex)
                    }));
                    alert('Document removed successfully');
                } else {
                    alert('Failed to remove document: ' + (result.data.message || 'Unknown error'));
                }
            })
            .catch((error) => {
                console.error('Error removing document:', error);
                alert('Error removing document. Please try again.');
            })
            .finally(() => {
                setRemovingDoc(null);
            });
    };

    const handleSubmitEdit = () => {
        if (!formData.course_name.trim()) {
            setEditError('Course Name is required');
            return;
        }

        setSubmitLoading(true);
        setEditError(null);

        const getRoleRemark = () => {
            if (role === 'principal') return formData.principal_remarks;
            if (role === 'aca16') return formData.aca16_remarks;
            // if (role === 'sfa')       return formData.sfa_remarks;
            return null;
        };

        const submitData = {
            application_id: formData.application_id,
            accountid: studentData.accountid,
            ABC_status: '1',
            course_name: formData.course_name,
            course_code: formData.course_code || null,
            discipline_id: studentApplicationData?.discipline_id || null,
            course_category: parseInt(formData.course_category) || null,
            course_level: formData.course_level,
            platform: formData.platform,
            mode: formData.mode.toLowerCase(),
            acquired_credits: (() => {
                const val = formData.acquired_credits;
                if (typeof val === 'string') return val;
                return parseFloat(val).toFixed(1);
            })(),
            grade: formData.grade,
            grade_point: parseInt(formData.grade_point) || 0,
            grade_scale: formData.grade_scale,
            percentage: formData.percentage ? parseFloat(formData.percentage) : null,
            application_status: studentApplicationData?.application_status || null,

            remarks: getRoleRemark(),
        };

        http.post('/College/UpdateStudentCreditApplicationDetails', submitData)
            // http.post(
            //     '/College/UpdateStudentCreditApplicationDetails/',
            //     submitData,
            //     {
            //         headers: {
            //             'Content-Type': 'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW',
            //         }
            //     }
            // )
            .then((result) => {
                if (result.data.status === true) {
                    // alert('Application updated successfully!');
                    toast.success("Application updated successfully!");
                    setTimeout(() => {
                        if (onUpdateSuccess) {
                            onUpdateSuccess(result.data);
                        }
                        if (onRefreshData) {
                            onRefreshData();
                        }
                        closeEditModal();
                    }, 2000); // 2 seconds delay
                } else {
                    toast.error("Failed to update application");

                    setTimeout(() => {
                        setEditError(result.data.message || 'Failed to update application');

                        // any other actions you want AFTER 2 sec
                        // e.g. close modal, reset form, etc.

                    }, 2000); //wait 2 seconds
                }
            })
            .catch((error) => {
                console.error('Error submitting edit:', error);
                setEditError('Network error. Please check your connection and try again.');
                alert('Network error. Please try again.');
            })
            .finally(() => {
                setSubmitLoading(false);
            });
    };

    const cancelEdit = () => {
        if (window.confirm('Discard changes? Unsaved data will be lost.')) {
            setDataLoaded(false);
            setFormData({
                application_id: null,
                course_name: '',
                course_category: '',
                course_level: '',
                platform: '',
                mode: '',
                acquired_credits: '',
                grade: '',
                grade_point: '',
                grade_scale: '',
                percentage: '',
                principal_remarks: '',
                aca16_remarks: '',
                documents: []
            });
            setStudentData(null);
            setEditError(null);
            closeEditModal();
        }
    };

    const handleClose = () => {
        setDataLoaded(false);
        setFormData({
            application_id: null,
            course_name: '',
            course_code: '',
            course_category: '',
            course_level: '',
            platform: '',
            mode: '',
            acquired_credits: '',
            grade: '',
            grade_point: '',
            grade_scale: '',
            percentage: '',
            principal_remarks: '',
            aca16_remarks: '',
            documents: []
        });
        setStudentData(null);
        setEditError(null);
        closeEditModal();
    };

    if (!showEditModal) return null;

    return (
        <>
            <ToastContainer />

            <div className="college_ext_cred_app">
                <div className="edit-modal-overlay" onClick={handleClose}>
                    <div className="edit-modal-dialog" onClick={e => e.stopPropagation()}>
                        <div className="edit-modal-content">
                            <div className="edit-modal-header">
                                <h3>
                                    <i className="fa fa-pencil-square-o"></i>
                                    Edit External Credit Application
                                </h3>
                                <button type="button" className="close-btn" onClick={handleClose}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <div className="edit-modal-body">
                                {loading ? (
                                    <div className="loading-container">
                                        <div className="spinner"></div>
                                        <p>Loading application details...</p>
                                    </div>
                                ) : (
                                    <>
                                        {studentData && (
                                            <div className="student-info-banner">
                                                <div className="student-info-item">
                                                    <span className="student-info-label">Student Name</span>
                                                    <span className="student-info-value">{studentData.studentname}</span>
                                                </div>
                                                <div className="student-info-item">
                                                    <span className="student-info-label">PRN</span>
                                                    <span className="student-info-value">{studentData.prn}</span>
                                                </div>
                                                <div className="student-info-item">
                                                    <span className="student-info-label">Admission Year</span>
                                                    <span className="student-info-value">{studentData.admissionyear}</span>
                                                </div>
                                                <div className="student-info-item">
                                                    <span className="student-info-label">Discipline</span>
                                                    <span className="student-info-value">{studentData.discipline_name}</span>
                                                </div>
                                                <div className="student-info-item">
                                                    <span className="student-info-label">Current Status</span>
                                                    {/* <span className="status-badge">{studentApplicationData.application_status || 'Pending'}</span> */}
                                                    <span className="status-badge">{applicationLabel || 'Pending'}</span>
                                                </div>
                                            </div>
                                        )}

                                        {editError && (
                                            <div className="error-message">
                                                <i className="fa fa-exclamation-triangle"></i>
                                                <span>{editError}</span>
                                            </div>
                                        )}

                                        <div className="form-section">
                                            <div className="form-section-title">
                                                <i className="fa fa-book"></i>
                                                Course Details
                                            </div>
                                            <div className="form-grid">
                                                <div className="form-field">
                                                    <label className="required">Course Name</label>
                                                    <input
                                                        type="text"
                                                        name="course_name"
                                                        value={formData.course_name}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter course name"
                                                    />
                                                </div>
                                                <div className="form-field">
                                                    <label>Course Code <span style={{ fontWeight: 'normal', color: '#6c757d', fontSize: '0.8em' }}>(Optional)</span></label>
                                                    <input
                                                        type="text"
                                                        name="course_code"
                                                        value={formData.course_code || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value === '' || /^[A-Za-z0-9\s]+$/.test(value)) {
                                                                handleInputChange(e);
                                                            }
                                                        }}
                                                        placeholder="e.g. CS101"
                                                        maxLength={20}
                                                    />
                                                </div>
                                                <div className="form-field">
                                                    <label>Course Category</label>
                                                    <select
                                                        name="course_category"
                                                        value={formData.course_category}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select Category</option>
                                                        {categoryOptions.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-field">
                                                    <label>NHEQF Level</label>
                                                    {/*  <input
                                                        type="text"
                                                        name="course_level"
                                                        value={formData.course_level}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g., 5, 6, 7"
                                                    /> */}

                                                    <select
                                                        name="course_level"
                                                        value={formData.course_level}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select NHEQF Level</option>
                                                        {nheqfOptions.map((item, index) => (
                                                            <option key={index} value={item.nheqf_level}>
                                                                {item.nheqf_level}
                                                            </option>
                                                        ))}
                                                    </select>

                                                </div>
                                                <div className="form-field">
                                                    <label>Platform</label>
                                                    <input
                                                        type="text"
                                                        name="platform"
                                                        value={formData.platform}
                                                        onChange={handleInputChange}
                                                        placeholder="SWAYAM / Coursera / etc."
                                                    />
                                                </div>
                                                <div className="form-field">
                                                    <label>Mode</label>
                                                    <select
                                                        name="mode"
                                                        value={formData.mode}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select Mode</option>
                                                        {modeOptions.map(mode => (
                                                            <option key={mode} value={mode}>{mode}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-field">
                                                    <label>Course Credit</label>
                                                    <select
                                                        name="acquired_credits"
                                                        value={formData.acquired_credits}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select Credit</option>
                                                        <option value="1.0">1.0</option>
                                                        <option value="1.5">1.5</option>
                                                        <option value="2.0">2.0</option>
                                                        <option value="2.5">2.5</option>
                                                        <option value="3.0">3.0</option>
                                                        <option value="3.5">3.5</option>
                                                        <option value="4.0">4.0</option>
                                                        <option value="4.5">4.5</option>
                                                        <option value="5.0">5.0</option>
                                                        <option value="5.5">5.5</option>
                                                        <option value="6.0">6.0</option>
                                                        <option value="6.5">6.5</option>
                                                        <option value="7.0">7.0</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <div className="form-section-title">
                                                <i className="fa fa-graduation-cap"></i>
                                                Grade & Credits
                                            </div>
                                            <div className="form-grid">
                                                <div className="form-field">
                                                    <label>Grade</label>
                                                    <input
                                                        type="text"
                                                        name="grade"
                                                        value={formData.grade}
                                                        onChange={handleInputChange}
                                                        placeholder="A+, A, B, etc."
                                                    />
                                                </div>
                                                <div className="form-field">
                                                    <label>Grade Point</label>
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        name="grade_point"
                                                        value={formData.grade_point}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g., 9, 8.5"
                                                    />
                                                </div>
                                                <div className="form-field">
                                                    <label>Grade Scale</label>
                                                    <input
                                                        type="text"
                                                        name="grade_scale"
                                                        value={formData.grade_scale}
                                                        onChange={handleInputChange}
                                                        placeholder="10-point scale"
                                                    />
                                                </div>
                                                <div className="form-field">
                                                    <label>Percentage</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="percentage"
                                                        value={formData.percentage}
                                                        onChange={handleInputChange}
                                                        placeholder="Percentage obtained"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Show Principal Remarks as readonly on ACA16 page */}
                                        {(role === 'principal' || formData.principal_remarks) && (
                                            <div className="form-section remarks-section">
                                                <div className="form-section-title">
                                                    <i className="fa fa-commenting"></i>
                                                    Principal Remarks
                                                </div>
                                                <div className="form-field full-width">
                                                    <textarea
                                                        name="principal_remarks"
                                                        value={formData.principal_remarks}
                                                        onChange={handleInputChange}
                                                        readOnly={role !== 'principal'}
                                                        style={role !== 'principal' ? {
                                                            backgroundColor: '#f5f5f5',
                                                            color: '#555',
                                                            cursor: 'not-allowed',
                                                            border: '1px solid #ccc'
                                                        } : {}}
                                                        placeholder="Principal remarks..."
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {/* ACA16 Remarks Section */}
                                        {(role === 'aca16' || formData.aca16_remarks) && (
                                            <div className="form-section remarks-section">
                                                <div className="form-section-title">
                                                    <i className="fa fa-commenting"></i>
                                                    ACA16 Remarks
                                                </div>
                                                <div className="form-field full-width">
                                                    <textarea
                                                        name="aca16_remarks"
                                                        value={formData.aca16_remarks}
                                                        onChange={handleInputChange}
                                                        readOnly={role !== 'aca16'}
                                                        style={role !== 'aca16' ? {
                                                            backgroundColor: '#f5f5f5',
                                                            color: '#555',
                                                            cursor: 'not-allowed',
                                                            border: '1px solid #ccc'
                                                        } : {}}
                                                        placeholder="ACA16 remarks..."
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {/* Attached documents section */}

                                        {/* <div className="form-section">
                                            <div className="form-section-title">
                                                <i className="fa fa-paperclip"></i>
                                                Attached Documents
                                            </div>
                                            <div className="documents-list">
                                                {formData.documents && formData.documents.length > 0 ? (
                                                    formData.documents.map((doc, idx) => (
                                                        <div key={idx} className="doc-item">
                                                            <div className="doc-name">
                                                                <i className="fa fa-file-pdf-o text-danger"></i>


                                                                <span className="badge" style={{ fontSize: '0.7rem', background: '#16C7F9', color: '#fff' }}>
                                                                    {doc.document_type?.toUpperCase()}
                                                                </span>
                                                                <span>{doc.file_name}</span>
                                                            </div>
                                                        
                                                            <button
                                                                type="button"
                                                                // className="btn btn-sm btn-outline-primary"
                                                                className="btn btn-info btn-sm mb-2"
                                                                onClick={() => openPdfModal(doc.encryptedFile, doc.file_name)}
                                                                disabled={pdfLoadingId === doc.document_id}
                                                            >
                                                                {pdfLoadingId === doc.document_id ? (
                                                                    <i className="fa fa-spinner fa-spin"></i>
                                                                ) : (
                                                                    <>
                                                                        <i className="fa fa-eye mr-1"></i> View
                                                                    </>
                                                                )}
                                                            </button>
                                                           
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="no-docs">
                                                        <i className="fa fa-info-circle"></i> No documents attached
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#6c757d', marginTop: '0.75rem' }}>
                                                 <i className="fa fa-shield"></i> Documents can be removed. To add new documents, please use the document upload section. 
                                            </div>
                                        </div>
                                        */ }
                                        {/* Attached documents section End */}

                                    </>
                                )}
                            </div>

                            {!loading && (
                                <div className="modal-actions">
                                    <button type="button" className="btn-cancel" onClick={handleClose}>
                                        <i className="fa fa-times"></i> Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-submit"
                                        onClick={handleSubmitEdit}
                                        disabled={submitLoading}
                                    >
                                        {submitLoading ? (
                                            <>
                                                <i className="fa fa-spinner fa-spin"></i>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-check"></i>
                                                Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>


                <PDFModal
                    isOpen={pdfModal.show}
                    onClose={closePdfModal}
                    pdfUrl={pdfModal.url}
                    fileName={pdfModal.name}
                    fileType={pdfModal.type}
                />

                <ViewApplicationModal
                    showViewModal={showViewModal}
                    closeViewModal={closeViewModal}
                    viewData={viewData}
                    viewLoading={viewLoading}
                    pdfLoading={pdfLoadingId}
                    pdfModal={pdfModal}
                    openPdfModal={openPdfModal}
                    closePdfModal={closePdfModal}
                />
            </div>
        </>
    );
}